/**
 * URL State Hook
 *
 * Manages dashboard state through URL parameters
 * All state is derived from and synced to the URL
 */

import { useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { parseFilterDSL, encodeFilterDSL } from "@/lib/routing/filter-dsl";
import type { DashboardState } from "@/lib/routing/types";

export function useURLState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo((): DashboardState => {
    const filterStr = searchParams.get("filter") || "";
    const sourcesStr = searchParams.get("sources") || "";

    return {
      period: searchParams.get("period") || "7d",
      filters: parseFilterDSL(filterStr),
      sources: sourcesStr ? sourcesStr.split(",") : [],
      view: searchParams.get("view") || undefined,
      tab: searchParams.get("tab") || undefined,
      url: searchParams.get("url") || undefined,
      viewport:
        (searchParams.get("viewport") as DashboardState["viewport"]) ||
        undefined,
    };
  }, [searchParams]);

  const updateState = useCallback(
    (updates: Partial<DashboardState>) => {
      const newParams = new URLSearchParams(searchParams);

      // Update period
      if (updates.period !== undefined) {
        newParams.set("period", updates.period);
      }

      // Update filters
      if (updates.filters !== undefined) {
        const encoded = encodeFilterDSL(updates.filters);
        if (encoded) {
          newParams.set("filter", encoded);
        } else {
          newParams.delete("filter");
        }
      }

      // Update sources
      if (updates.sources !== undefined) {
        if (updates.sources.length > 0) {
          newParams.set("sources", updates.sources.join(","));
        } else {
          newParams.delete("sources");
        }
      }

      // Update other params
      if (updates.view !== undefined) {
        newParams.set("view", updates.view);
      }
      if (updates.tab !== undefined) {
        newParams.set("tab", updates.tab);
      }
      if (updates.url !== undefined) {
        newParams.set("url", updates.url);
      }
      if (updates.viewport !== undefined) {
        newParams.set("viewport", updates.viewport);
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return { state, updateState };
}
